import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Table, Button, Pagination, Alert, Form, Card } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { DeleteModal } from '../../../Common/DeleteModal';
import { usePagination } from '../../../hooks/usePagination';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  listFeatureToggles,
  deleteFeatureToggle,
  createOrUpdateFeatureToggle,
  FeatureToggle
} from '../../../api/featureManagement/featureToggleService';
import { listFeatures } from '../../../api/featureManagement/featureService';
import { getImageUrl } from '../../../utils/imageUrl';
import FeatureToggleFormModal from './FeatureToggleFormModal';
import { PermissionWrapper } from '../../../components/PermissionWrapper/PermissionWrapper';
import '../FeatureManagement.css';
import './FeatureToggles.css';

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'community', label: 'Community' },
  { value: 'payment', label: 'Payment' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ENABLED', label: 'Enabled' },
  { value: 'DISABLED', label: 'Disabled' },
];

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const FeatureTogglesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.Login);
  const clientId = process.env.REACT_APP_CLIENT_ID || user?.clientId || '';
  
  // Pagination
  const {
    page,
    pageSize,
    totalPages,
    totalCount,
    setPage,
    setPageSize,
    setTotalPages,
    setTotalCount,
    handlePageChange
  } = usePagination(1, 10);

  // State
  const [toggles, setToggles] = useState<FeatureToggle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [scopeFilter, setScopeFilter] = useState<{ value: string; label: string }>(SCOPE_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState<{ value: string; label: string }>(STATUS_OPTIONS[0]);
  
  // Summary stats
  const [totalFeatures, setTotalFeatures] = useState(0);
  const [enabledCount, setEnabledCount] = useState(0);
  const [disabledCount, setDisabledCount] = useState(0);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeatureToggle | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Form modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingToggle, setEditingToggle] = useState<FeatureToggle | null>(null);

  // Fetch toggles
  const fetchToggles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        // clientId will be added automatically by the interceptor
        // Don't send page/pageSize to get all feature toggles
        // The API will return all matching feature toggles
      };
      
      if (scopeFilter.value !== 'all') {
        params.scope = scopeFilter.value;
      }

      const response: any = await listFeatureToggles(params);
      let allToggles: any[] = response.toggles || [];

      // Apply client-side status filtering (by enabled flag)
      if (statusFilter.value !== 'all') {
        const wantEnabled = statusFilter.value === 'ENABLED';
        allToggles = allToggles.filter((t) => !!t.enabled === wantEnabled);
      }

      // Apply client-side search filtering (feature id/name, entity type/value)
      if (debouncedSearch.trim()) {
        const searchLower = debouncedSearch.toLowerCase().trim();
        allToggles = allToggles.filter((t) => {
          const featureId = (t.featureid || '').toLowerCase();
          const featureName = (t.feature?.name || '').toLowerCase();
          const entityType = (t.entitytype || '').toLowerCase();
          const entityValue = (t.entityvalue || '').toLowerCase();
          return (
            featureId.includes(searchLower) ||
            featureName.includes(searchLower) ||
            entityType.includes(searchLower) ||
            entityValue.includes(searchLower)
          );
        });
      }
      
      const totalCount = allToggles.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      setTotalPages(totalPages);
      setTotalCount(totalCount);
      
      // Apply client-side pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedToggles = allToggles.slice(startIndex, endIndex);
      setToggles(paginatedToggles as any);

      // Calculate summary stats from all data (now we have all toggles)
      setTotalFeatures(totalCount);
      const enabledCount = allToggles.filter(t => !!t.enabled).length;
      const disabledCount = allToggles.filter(t => !t.enabled).length;
      
      // Now we have accurate counts from all data
      setEnabledCount(enabledCount);
      setDisabledCount(disabledCount);
    } catch (err: any) {
      console.error('Error fetching feature toggles:', err);
      setError(err.message || 'Failed to load feature toggles. Please try again.');
      setToggles([]);
      setTotalPages(1);
      setTotalCount(0);
      setTotalFeatures(0);
      setEnabledCount(0);
      setDisabledCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, scopeFilter, statusFilter]);

  useEffect(() => {
    fetchToggles();
  }, [fetchToggles]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, scopeFilter, statusFilter]);

  // Handlers
  const handleCreate = () => {
    setEditingToggle(null);
    setShowFormModal(true);
  };

  const handleEdit = (toggle: FeatureToggle) => {
    setEditingToggle(toggle);
    setShowFormModal(true);
  };

  const handleDelete = (toggle: FeatureToggle) => {
    setDeleteTarget(toggle);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const t = deleteTarget as any;
    try {
      setIsDeleting(true);
      setDeleteError(null);

      await deleteFeatureToggle({
        featureId: t.featureid || t.featureId,
        entityType: t.entitytype || t.entityType,
        entityValue: t.entityvalue || t.entityValue,
      });
      
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchToggles();
    } catch (err: any) {
      console.error('Error deleting feature toggle:', err);
      setDeleteError(err.message || 'Failed to delete feature toggle. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (toggle: FeatureToggle, e: React.MouseEvent) => {
    e.stopPropagation();
    const t = toggle as any;
    const featureName = t.feature?.name || t.featureid || '';
    const featureScope = t.feature?.scope || t.scope || t.featurescope || scopeFilter.value;
    const conditions = t.conditions || [];
    const hasConditions = conditions.length > 0;

    const payload = {
      featureName,
      featureScope: featureScope === 'all' ? 'tournament' : featureScope,
      clientId: t.feature?.clientId || t.feature?.clientid,
      entityType: t.entitytype || '',
      entityValue: t.entityvalue || '',
      enabled: !t.enabled,
      conditions: hasConditions
        ? conditions.map((c: any) => ({
            name: c.name,
            comparator: c.comparator || 'EQ',
            value: c.value,
          }))
        : undefined,
      setConditions: hasConditions,
    };

    try {
      await createOrUpdateFeatureToggle(payload);
      fetchToggles();
    } catch (err: any) {
      console.error('Error toggling feature toggle status:', err);
      setError(err.message || 'Failed to toggle feature toggle status. Please try again.');
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} active={page === 1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item key={p} active={page === p} onClick={() => handlePageChange(p)}>
          {p}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} active={page === totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} feature toggles
        </div>
        <Pagination className="mb-0">
          <Pagination.First disabled={page === 1} onClick={() => handlePageChange(1)} />
          <Pagination.Prev disabled={page === 1} onClick={() => handlePageChange(page - 1)} />
          {items}
          <Pagination.Next disabled={page === totalPages} onClick={() => handlePageChange(page + 1)} />
          <Pagination.Last disabled={page === totalPages} onClick={() => handlePageChange(totalPages)} />
        </Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="feature-management-page">
      {/* Breadcrumbs */}
      <div className="mb-3 mt-4" style={{ padding: '0 15px' }}>
        <Breadcrumbs
          items={[
            { label: 'Feature Management', onClick: () => navigate('/feature-management') },
            { label: 'Feature Toggles', active: true }
          ]}
        />
      </div>

      {/* Summary Cards */}
      <div style={{ padding: '0 15px', marginTop: '28px', marginBottom: '24px' }}>
        <Row className="mb-4">
          <Col md={4}>
            <Card style={{ border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Card.Body className="feature-toggle-summary-card-body">
                <div className="feature-toggle-summary-card-icon feature-toggle-summary-card-icon--total">
                  <img
                  src={getImageUrl('shared/assets/icons/export-file-01.svg')}
                  alt="Export"
                  style={{ width: '2rem', height: '2rem' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallbackIcon = document.createElement('i');
                    fallbackIcon.className = 'ph-file-arrow-down';
                    target.parentElement?.appendChild(fallbackIcon);
                  }}
                />
                </div>
                <div className="feature-toggle-summary-card-text">
                  <h4 className="mb-0 feature-toggle-summary-card-number">{totalFeatures}</h4>
                  <p className="text-muted mb-0">Total Features</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card style={{ border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Card.Body className="feature-toggle-summary-card-body">
                <div className="feature-toggle-summary-card-icon feature-toggle-summary-card-icon--enabled">
                  <img
                    src={getImageUrl('shared/assets/icons/export-file-validation.svg')}
                    alt="Enabled"
                    style={{ width: '2rem', height: '2rem' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallbackIcon = document.createElement('i');
                      fallbackIcon.className = 'ph-check-circle';
                      target.parentElement?.appendChild(fallbackIcon);
                    }}
                  />
                </div>
                <div className="feature-toggle-summary-card-text">
                  <h4 className="mb-0 feature-toggle-summary-card-number feature-toggle-summary-card-number--enabled">
                    {enabledCount}
                  </h4>
                  <p className="text-muted mb-0">Enabled Features</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card style={{ border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Card.Body className="feature-toggle-summary-card-body">
                <div className="feature-toggle-summary-card-icon feature-toggle-summary-card-icon--disabled">
                  <img
                    src={getImageUrl('shared/assets/icons/export-file-block.svg')}
                    alt="Disabled"
                    style={{ width: '2rem', height: '2rem' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallbackIcon = document.createElement('i');
                      fallbackIcon.className = 'ph-prohibit';
                      target.parentElement?.appendChild(fallbackIcon);
                    }}
                  />
                </div>
                <div className="feature-toggle-summary-card-text">
                  <h4 className="mb-0 feature-toggle-summary-card-number feature-toggle-summary-card-number--disabled">
                    {disabledCount}
                  </h4>
                  <p className="text-muted mb-0">Disabled Features</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Header + Filters */}
      <div style={{ padding: '0 15px', marginBottom: '24px' }}>
        <Row className="align-items-center justify-content-between g-3">
          <Col xs="12" md="auto">
            <h2 className="mb-0">Feature Toggles</h2>
          </Col>
          <Col xs="12" md="8">
            <Row className="g-2 justify-content-md-end align-items-center flex-nowrap">
              <Col xs={12} md={5} className="min-w-0">
                <Form.Control
                  type="text"
                  placeholder="Search feature toggles, Entity"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col xs={6} md={2} className="flex-shrink-0">
                <Select
                  classNamePrefix="react-select"
                  value={scopeFilter}
                  onChange={(option) => setScopeFilter(option || SCOPE_OPTIONS[0])}
                  options={SCOPE_OPTIONS}
                  isClearable={false}
                />
              </Col>
              <Col xs={6} md={2} className="flex-shrink-0">
                <Select
                  classNamePrefix="react-select"
                  value={statusFilter}
                  onChange={(option) => setStatusFilter(option || STATUS_OPTIONS[0])}
                  options={STATUS_OPTIONS}
                  isClearable={false}
                />
              </Col>
              <Col xs={12} md="auto" className="flex-shrink-0 text-md-end d-flex align-items-stretch">
              <PermissionWrapper
                  anyOfPermissions={[
                    'feature_management.admin',
                    'feature_management.feature_toggles.manage',
                  ]}
                  hideWhenDisabled
                >
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  className="d-flex align-items-center justify-content-center"
                >
                  Create New Feature Toggle +
                </Button>
                </PermissionWrapper>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{ padding: '0 15px', marginBottom: '16px' }}>
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Table */}
      <div style={{ padding: '0 15px' }}>
        {loading ? (
          <ShimmerLoader type="table" rows={5} />
        ) : (
          <>
            <div style={{ backgroundColor: '#ffffff', padding: '16px', borderRadius: '16px' }}>
              <Table responsive hover className="feature-toggles-table mb-0">
                <thead>
                  <tr>
                    <th>Feature ID</th>
                    <th>Feature Name</th>
                    <th>Entity Type</th>
                    <th>Entity Value</th>
                    <th>Conditions</th>
                    <th>Created at</th>
                    <th>Updated at</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {toggles.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-5">
                        <div className="empty-state">
                          <i className="ri-inbox-line" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}></i>
                          <h5>No feature toggles found</h5>
                          <p className="text-muted">Get started by creating your first feature toggle.</p>
                          <PermissionWrapper
                            anyOfPermissions={[
                              'feature_management.admin',
                              'feature_management.feature_toggles.manage',
                            ]}
                            hideWhenDisabled
                          >
                          <Button variant="primary" onClick={handleCreate}>
                            Create New Feature Toggle
                          </Button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    toggles.map((toggle) => {
                      const t = toggle as any;
                      const featureId = t.featureid;
                      const featureName = t.feature?.name || featureId;
                      const entityType = t.entitytype;
                      const entityValue = t.entityvalue;
                      const conditions = t.conditions || [];
                      const createdAt = t.createdat;
                      const updatedAt = t.updatedat;
                      const enabled = !!t.enabled;

                      return (
                        <tr key={toggle._id}>
                          <td>{featureId}</td>
                          <td>{featureName}</td>
                          <td>{entityType}</td>
                          <td>{entityValue}</td>
                          <td>
                            {conditions && conditions.length > 0
                              ? `${conditions.length} condition(s)`
                              : '-'}
                          </td>
                          <td>
                            {createdAt
                              ? new Date(createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '-'}
                          </td>
                          <td>
                            {updatedAt
                              ? new Date(updatedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '-'}
                          </td>
                          <td>
                          <PermissionWrapper
                            anyOfPermissions={[
                              'feature_management.admin',
                              'feature_management.feature_toggles.manage',
                            ]}
                            hideWhenDisabled
                          >
                            <Form.Check
                              type="switch"
                              id={`toggle-${toggle._id}`}
                              checked={enabled}
                              onChange={(e) => handleToggleStatus(toggle, e as any)}
                              label={enabled ? 'Enabled' : 'Disabled'}
                            />
                            </PermissionWrapper>
                          </td>
                          <td>
                            <div className="feature-action-btns d-flex">
                            <PermissionWrapper
                            anyOfPermissions={[
                              'feature_management.admin',
                              'feature_management.feature_toggles.manage',
                            ]}
                            hideWhenDisabled
                          >
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleEdit(toggle)}
                                title="Edit"
                              >
                                <img
                                  src={getImageUrl('shared/assets/icons/edit-outlined.svg')}
                                  alt="Edit"
                                  style={{ width: 24, height: 24 }}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallbackIcon = document.createElement('i');
                                    fallbackIcon.className = 'ph-pencil-simple';
                                    target.parentElement?.appendChild(fallbackIcon);
                                  }}
                                />
                              </Button>
                              </PermissionWrapper>
                              <PermissionWrapper
                              anyOfPermissions={[
                                'feature_management.admin',
                                'feature_management.feature_toggles.manage',
                              ]}
                              hideWhenDisabled
                            >
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleDelete(toggle)}
                                title="Delete"
                              >
                                <img
                                  src={getImageUrl('shared/assets/icons/delete-outline.svg')}
                                  alt="Delete"
                                  style={{ width: 24, height: 24 }}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallbackIcon = document.createElement('i');
                                    fallbackIcon.className = 'ph-trash';
                                    target.parentElement?.appendChild(fallbackIcon);
                                  }}
                                />
                              </Button>
                              </PermissionWrapper>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
              {renderPagination()}
            </div>
          </>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        deleteModalFunction={confirmDelete}
        title="Delete Feature Toggle"
        message={`Are you sure you want to delete this feature toggle? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />

      {/* Feature Toggle Form Modal */}
      <FeatureToggleFormModal
        show={showFormModal}
        handleClose={() => {
          setShowFormModal(false);
          setEditingToggle(null);
        }}
        onSuccess={() => {
          fetchToggles();
        }}
        toggle={editingToggle}
        clientId={clientId}
      />
    </Container>
  );
};

export default FeatureTogglesListPage;
